import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Row, Col, Button, Space, message, Tooltip, Collapse, List, Tag, Empty, Badge, App, Card } from "antd";
import {
  UndoOutlined,
  RedoOutlined,
  SaveOutlined,
  ReloadOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  PlusOutlined,
  ShoppingOutlined,
  UserOutlined,
  EnvironmentOutlined,
  StarFilled,
  AimOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import TripOrderList from "../trip-order-list";
import TripRouteMap, { IMapOrder } from "../trip-route-map";
import { IMapPolylines } from "../../../models/trip/response";
import TripRouteSummary from "../trip-route-summary";
import { ITripOrderCardData } from "../trip-order-card";
import { ILatLng, locationToLatLng, calculateDistanceBetweenPoints, decodePolyline } from "../../../utils/geo/haversine";
import {
  calculateDistancesAfterReorder,
  IRouteStop,
  findNearestPointOnRoute,
} from "../../../utils/geo/route-optimizer";
import { IOrderDetails } from "../../../models/order/response";
import { ILocation } from "../../../models/store/response";
import { getOrderStatus, getOrderStatusColor } from "../../../models/order/enum";
import { RouteStatus } from "../../general/map/route-polyline";
import styles from "./style.module.scss";

export interface ITripRoutePlannerOrder {
  id: number;
  number: string;
  status: string;
  sequence?: number;
  distanceToNext?: number;
  client?: {
    firstName?: string;
    lastName?: string;
    mobile?: string;
  };
  clientAddress?: string;
  location?: ILocation;
}

export interface ITripRoutePlannerProps {
  storeLocation?: ILocation;
  assignedOrders: ITripRoutePlannerOrder[];
  unassignedOrders?: IOrderDetails[];
  isEditable?: boolean;
  isLoading?: boolean;
  /** New mapPolylines structure with segments */
  mapPolylines?: IMapPolylines | null;
  routeStatus?: RouteStatus;
  onOrdersChange?: (orders: ITripRoutePlannerOrder[]) => void;
  onSave?: (orderIds: number[]) => void;
  onAddOrder?: (order: IOrderDetails) => void;
  onRemoveOrder?: (orderId: number) => void;
}

interface HistoryState {
  orders: ITripRoutePlannerOrder[];
}

const MAX_HISTORY = 20;

const TripRoutePlanner: React.FC<ITripRoutePlannerProps> = ({
  storeLocation,
  assignedOrders: initialAssignedOrders,
  unassignedOrders = [],
  isEditable = true,
  isLoading = false,
  mapPolylines,
  routeStatus = "pending",
  onOrdersChange,
  onSave,
  onAddOrder,
  onRemoveOrder,
}) => {
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const [assignedOrders, setAssignedOrders] = useState<ITripRoutePlannerOrder[]>(
    initialAssignedOrders
  );
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [history, setHistory] = useState<HistoryState[]>([
    { orders: initialAssignedOrders },
  ]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setAssignedOrders(initialAssignedOrders);
    setHistory([{ orders: initialAssignedOrders }]);
    setHistoryIndex(0);
  }, [initialAssignedOrders]);

  const storeLatLng = useMemo<ILatLng | undefined>(() => {
    if (!storeLocation) return undefined;
    return locationToLatLng(storeLocation);
  }, [storeLocation]);

  const cardOrders = useMemo<ITripOrderCardData[]>(() => {
    // Sort orders by sequence for distance calculation
    const sortedOrders = [...assignedOrders].sort(
      (a, b) => (a.sequence || 0) - (b.sequence || 0)
    );

    return sortedOrders.map((order, index) => {
      let distanceFromPrevious = 0;

      if (order.location) {
        const currentLocation = locationToLatLng(order.location);

        if (index === 0) {
          // First order: calculate distance from store
          if (storeLatLng) {
            distanceFromPrevious = calculateDistanceBetweenPoints(
              storeLatLng,
              currentLocation
            );
          }
        } else {
          // Subsequent orders: calculate distance from previous order
          const prevOrder = sortedOrders[index - 1];
          if (prevOrder?.location) {
            const prevLocation = locationToLatLng(prevOrder.location);
            distanceFromPrevious = calculateDistanceBetweenPoints(
              prevLocation,
              currentLocation
            );
          }
        }
      }

      return {
        id: order.id,
        number: order.number,
        status: getOrderStatus(Number(order.status)),
        statusColor: getOrderStatusColor(Number(order.status)),
        sequence: order.sequence || 0,
        distanceToNext: order.distanceToNext || 0,
        distanceFromPrevious,
        client: order.client,
        clientAddress: order.clientAddress,
      };
    });
  }, [assignedOrders, storeLatLng]);

  const mapAssignedOrders = useMemo<IMapOrder[]>(() => {
    return assignedOrders
      .filter((order) => order.location)
      .map((order) => ({
        id: order.id,
        number: order.number,
        clientName: order.client
          ? `${order.client.firstName || ""} ${order.client.lastName || ""}`.trim()
          : undefined,
        clientAddress: order.clientAddress,
        sequence: order.sequence,
        status: order.status,
        location: locationToLatLng(order.location!),
      }));
  }, [assignedOrders]);

  const mapUnassignedOrders = useMemo<IMapOrder[]>(() => {
    return unassignedOrders
      .filter((order) => order.location)
      .map((order) => ({
        id: order.id,
        number: order.number,
        clientName: order.client
          ? `${order.client.firstName || ""} ${order.client.lastName || ""}`.trim()
          : undefined,
        clientAddress: order.clientAddress,
        status: order.status?.toString(),
        location: locationToLatLng(order.location),
      }));
  }, [unassignedOrders]);

  // Decode the polyline segments and build route path for distance calculations
  const routePathForDistance = useMemo(() => {
    // Decode all segments from mapPolylines
    if (mapPolylines?.segments?.length) {
      const allPoints: ILatLng[] = [];
      const sortedSegments = [...mapPolylines.segments].sort((a, b) => a.index - b.index);

      sortedSegments.forEach((segment) => {
        if (!segment.polyline) return;
        const decoded = decodePolyline(segment.polyline);
        decoded.forEach((point, idx) => {
          // Avoid duplicates at segment boundaries
          if (allPoints.length === 0 || idx > 0) {
            allPoints.push(point);
          }
        });
      });

      if (allPoints.length > 0) {
        return allPoints;
      }
    }

    // Fallback to simple path from store to assigned orders and back (round trip)
    const path: ILatLng[] = [];
    if (storeLatLng) {
      path.push(storeLatLng);
    }
    const sortedOrders = assignedOrders
      .filter((o) => o.location)
      .sort((a, b) => (a.sequence || 0) - (b.sequence || 0));

    sortedOrders.forEach((order) => {
      if (order.location) {
        path.push(locationToLatLng(order.location));
      }
    });

    // Add return to store (round trip)
    if (storeLatLng && sortedOrders.length > 0) {
      path.push(storeLatLng);
    }
    return path;
  }, [mapPolylines, storeLatLng, assignedOrders]);

  // Calculate recommended orders based on proximity to route
  const recommendedOrdersData = useMemo(() => {
    if (unassignedOrders.length === 0) {
      return { orders: [] as (IOrderDetails & { distanceFromReference?: number })[], recommendedIds: [] as number[] };
    }

    // If we have a route with at least 2 points, use route-based distance
    const useRouteDistance = routePathForDistance.length >= 2;

    if (useRouteDistance) {
      // Calculate distance to route for all unassigned orders
      const ordersWithDistance = unassignedOrders.map((order) => {
        if (!order.location) {
          return { ...order, distanceFromReference: undefined };
        }

        try {
          const result = findNearestPointOnRoute(
            locationToLatLng(order.location),
            routePathForDistance
          );
          return {
            ...order,
            distanceFromReference: result.distanceToRoute,
          };
        } catch (error) {
          console.error("Error calculating distance to route:", error);
          return { ...order, distanceFromReference: undefined };
        }
      });

      // Sort by distance to route
      const sortedOrders = [...ordersWithDistance].sort((a, b) => {
        if (a.distanceFromReference === undefined && b.distanceFromReference === undefined) return 0;
        if (a.distanceFromReference === undefined) return 1;
        if (b.distanceFromReference === undefined) return -1;
        return a.distanceFromReference - b.distanceFromReference;
      });

      // Get top 3 closest orders to route as recommendations
      const recommendedIds = sortedOrders
        .filter((o) => o.distanceFromReference !== undefined)
        .slice(0, 3)
        .map((o) => o.id);

      return {
        orders: sortedOrders,
        recommendedIds,
      };
    }

    // Fallback: Use distance from reference point (last order or store)
    const lastAssignedOrder = assignedOrders
      .filter((o) => o.location)
      .sort((a, b) => (b.sequence || 0) - (a.sequence || 0))[0];

    let referencePoint: ILatLng | undefined;
    if (lastAssignedOrder?.location) {
      referencePoint = locationToLatLng(lastAssignedOrder.location);
    } else if (storeLatLng) {
      referencePoint = storeLatLng;
    }

    if (!referencePoint) {
      return {
        orders: unassignedOrders.map(o => ({ ...o, distanceFromReference: undefined })),
        recommendedIds: [] as number[]
      };
    }

    const ordersWithDistance = unassignedOrders.map((order) => ({
      ...order,
      distanceFromReference: order.location
        ? calculateDistanceBetweenPoints(
            referencePoint!,
            locationToLatLng(order.location)
          )
        : undefined,
    }));

    const sortedOrders = [...ordersWithDistance].sort((a, b) => {
      if (a.distanceFromReference === undefined && b.distanceFromReference === undefined) return 0;
      if (a.distanceFromReference === undefined) return 1;
      if (b.distanceFromReference === undefined) return -1;
      return a.distanceFromReference - b.distanceFromReference;
    });

    const recommendedIds = sortedOrders
      .filter((o) => o.distanceFromReference !== undefined)
      .slice(0, 3)
      .map((o) => o.id);

    return {
      orders: sortedOrders,
      recommendedIds,
    };
  }, [storeLatLng, unassignedOrders, assignedOrders, routePathForDistance]);

  const orderLocations = useMemo<ILatLng[]>(() => {
    return assignedOrders
      .filter((order) => order.location)
      .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
      .map((order) => locationToLatLng(order.location!));
  }, [assignedOrders]);

  const pushHistory = useCallback(
    (orders: ITripRoutePlannerOrder[]) => {
      setHistory((prev) => {
        const newHistory = prev.slice(0, historyIndex + 1);
        newHistory.push({ orders });
        if (newHistory.length > MAX_HISTORY) {
          newHistory.shift();
          return newHistory;
        }
        return newHistory;
      });
      setHistoryIndex((prev) => Math.min(prev + 1, MAX_HISTORY - 1));
    },
    [historyIndex]
  );

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  const handleUndo = useCallback(() => {
    if (!canUndo) return;
    const newIndex = historyIndex - 1;
    setHistoryIndex(newIndex);
    const prevState = history[newIndex];
    setAssignedOrders(prevState.orders);
    onOrdersChange?.(prevState.orders);
  }, [canUndo, historyIndex, history, onOrdersChange]);

  const handleRedo = useCallback(() => {
    if (!canRedo) return;
    const newIndex = historyIndex + 1;
    setHistoryIndex(newIndex);
    const nextState = history[newIndex];
    setAssignedOrders(nextState.orders);
    onOrdersChange?.(nextState.orders);
  }, [canRedo, historyIndex, history, onOrdersChange]);

  const recalculateDistances = useCallback(
    (orders: ITripRoutePlannerOrder[]): ITripRoutePlannerOrder[] => {
      if (!storeLatLng || orders.length === 0) {
        return orders;
      }

      const stops: IRouteStop[] = orders
        .filter((o) => o.location)
        .map((order) => ({
          id: order.id,
          location: locationToLatLng(order.location!),
          sequence: order.sequence || 0,
        }));

      const distances = calculateDistancesAfterReorder(storeLatLng, stops);
      const distanceMap = new Map(distances.map((d) => [d.id, d.distanceToNext]));

      return orders.map((order) => ({
        ...order,
        distanceToNext: distanceMap.get(order.id) || 0,
      }));
    },
    [storeLatLng]
  );

  const handleOrdersReorder = useCallback(
    (reorderedCardOrders: ITripOrderCardData[]) => {
      const updatedOrders = reorderedCardOrders.map((cardOrder, index) => {
        const originalOrder = assignedOrders.find((o) => o.id === cardOrder.id);
        return {
          ...originalOrder!,
          sequence: index + 1,
        };
      });

      const ordersWithDistances = recalculateDistances(updatedOrders);
      setAssignedOrders(ordersWithDistances);
      pushHistory(ordersWithDistances);
      onOrdersChange?.(ordersWithDistances);
    },
    [assignedOrders, recalculateDistances, pushHistory, onOrdersChange]
  );

  const handleOrderRemove = useCallback(
    (orderId: number) => {
      modal.confirm({
        title: t("confirm_remove"),
        content: t("confirm_remove_order_from_trip"),
        okText: t("remove"),
        okType: "danger",
        cancelText: t("cancel"),
        onOk: () => {
          const updatedOrders = assignedOrders
            .filter((o) => o.id !== orderId)
            .map((order, index) => ({
              ...order,
              sequence: index + 1,
            }));

          const ordersWithDistances = recalculateDistances(updatedOrders);
          setAssignedOrders(ordersWithDistances);
          pushHistory(ordersWithDistances);
          onOrdersChange?.(ordersWithDistances);
          onRemoveOrder?.(orderId);

          if (selectedOrderId === orderId) {
            setSelectedOrderId(null);
          }
        },
      });
    },
    [
      modal,
      t,
      assignedOrders,
      recalculateDistances,
      pushHistory,
      onOrdersChange,
      onRemoveOrder,
      selectedOrderId,
    ]
  );

  const handleOrderAdd = useCallback(
    (orderId: number) => {
      const orderToAdd = unassignedOrders.find((o) => o.id === orderId);
      if (!orderToAdd) return;

      const newOrder: ITripRoutePlannerOrder = {
        id: orderToAdd.id,
        number: orderToAdd.number,
        status: orderToAdd.status?.toString() || "pending",
        sequence: assignedOrders.length + 1,
        distanceToNext: 0,
        client: orderToAdd.client,
        clientAddress: orderToAdd.clientAddress,
        location: orderToAdd.location,
      };

      const updatedOrders = [...assignedOrders, newOrder];
      const ordersWithDistances = recalculateDistances(updatedOrders);
      setAssignedOrders(ordersWithDistances);
      pushHistory(ordersWithDistances);
      onOrdersChange?.(ordersWithDistances);
      onAddOrder?.(orderToAdd);
    },
    [
      unassignedOrders,
      assignedOrders,
      recalculateDistances,
      pushHistory,
      onOrdersChange,
      onAddOrder,
    ]
  );

  const handleOrderSelect = useCallback((orderId: number) => {
    setSelectedOrderId((prev) => (prev === orderId ? null : orderId));
  }, []);

  const handleSave = useCallback(async () => {
    if (!onSave) return;

    setIsSaving(true);
    try {
      const orderIds = assignedOrders
        .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
        .map((o) => o.id);
      await onSave(orderIds);
      message.success(t("trip_saved_successfully"));
    } catch (error) {
      message.error(t("failed_to_save_trip"));
    } finally {
      setIsSaving(false);
    }
  }, [onSave, assignedOrders, t]);

  const handleRefresh = useCallback(() => {
    setAssignedOrders(initialAssignedOrders);
    setHistory([{ orders: initialAssignedOrders }]);
    setHistoryIndex(0);
    setSelectedOrderId(null);
    message.info(t("trip_reset"));
  }, [initialAssignedOrders, t]);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => !prev);
  }, []);

  return (
    <div
      className={`${styles.plannerContainer} ${
        isFullscreen ? styles.fullscreen : ""
      }`}
    >
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <Space>
          <Tooltip title={t("undo")}>
            <Button
              icon={<UndoOutlined />}
              disabled={!canUndo || !isEditable}
              onClick={handleUndo}
            />
          </Tooltip>
          <Tooltip title={t("redo")}>
            <Button
              icon={<RedoOutlined />}
              disabled={!canRedo || !isEditable}
              onClick={handleRedo}
            />
          </Tooltip>
          <Tooltip title={t("reset")}>
            <Button
              icon={<ReloadOutlined />}
              disabled={!isEditable}
              onClick={handleRefresh}
            />
          </Tooltip>
        </Space>

        <Space>
          <Tooltip title={isFullscreen ? t("exit_fullscreen") : t("fullscreen")}>
            <Button
              icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={toggleFullscreen}
            />
          </Tooltip>
          {onSave && (
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={isSaving}
              disabled={!isEditable || assignedOrders.length === 0}
              onClick={handleSave}
            >
              {t("save")}
            </Button>
          )}
        </Space>
      </div>

      {/* Route Summary */}
      <div className={styles.summarySection}>
        <TripRouteSummary
          storeLocation={storeLatLng}
          orderLocations={orderLocations}
          orderCount={assignedOrders.length}
          showCard={false}
        />
      </div>

      {/* Available Orders Panel - Always visible when editable */}
      {isEditable && (
        <div className={styles.availableOrdersSection}>
          <Collapse
            defaultActiveKey={unassignedOrders.length > 0 ? ["available"] : []}
            items={[
              {
                key: "available",
                label: (
                  <Space>
                    <ShoppingOutlined />
                    <span>{t("available_orders")}</span>
                    <Badge
                      count={unassignedOrders.length}
                      style={{ backgroundColor: unassignedOrders.length > 0 ? "#52c41a" : "#d9d9d9" }}
                      showZero
                    />
                  </Space>
                ),
                children: (
                  <List
                    className={styles.availableOrdersList}
                    dataSource={recommendedOrdersData.orders}
                    locale={{ emptyText: <Empty description={t("no_available_orders")} /> }}
                    renderItem={(order: any) => {
                      const clientName = order.client
                        ? `${order.client.firstName || ""} ${order.client.lastName || ""}`.trim()
                        : "";
                      const isRecommended = recommendedOrdersData.recommendedIds.includes(order.id);
                      const distance = order.distanceFromReference;
                      const isOrderSelected = selectedOrderId === order.id;
                      return (
                        <List.Item
                          key={order.id}
                          className={`${styles.availableOrderItem} ${isRecommended ? styles.recommendedOrder : ""} ${isOrderSelected ? styles.selectedOrder : ""}`}
                          onClick={() => handleOrderSelect(order.id)}
                          style={{ cursor: "pointer" }}
                          actions={[
                            <Button
                              key="add"
                              type="primary"
                              size="small"
                              icon={<PlusOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOrderAdd(order.id);
                              }}
                              style={isRecommended ? { backgroundColor: "#52c41a", borderColor: "#52c41a" } : undefined}
                            >
                              {t("add")}
                            </Button>,
                          ]}
                        >
                          <List.Item.Meta
                            avatar={isRecommended ? (
                              <div className={styles.recommendedBadge}>
                                <StarFilled style={{ color: "#faad14", fontSize: 16 }} />
                              </div>
                            ) : null}
                            title={
                              <Space>
                                <Tag color={isRecommended ? "green" : "blue"}>#{order.number}</Tag>
                                <Tag color={getOrderStatusColor(Number(order.status))}>
                                  {t(getOrderStatus(Number(order.status)))}
                                </Tag>
                                {isRecommended && (
                                  <Tag color="gold" icon={<StarFilled />}>
                                    {t("recommended")}
                                  </Tag>
                                )}
                              </Space>
                            }
                            description={
                              <Space direction="vertical" size={2}>
                                {clientName && (
                                  <span>
                                    <UserOutlined style={{ marginRight: 4 }} />
                                    {clientName}
                                  </span>
                                )}
                                {order.clientAddress && (
                                  <span style={{ fontSize: 12, color: "#888" }}>
                                    <EnvironmentOutlined style={{ marginRight: 4 }} />
                                    {order.clientAddress}
                                  </span>
                                )}
                                {distance !== undefined && (
                                  <span style={{ fontSize: 12, color: isRecommended ? "#52c41a" : "#888" }}>
                                    <AimOutlined style={{ marginRight: 4 }} />
                                    {distance.toFixed(2)} {t("km")} {t("from_route")}
                                  </span>
                                )}
                              </Space>
                            }
                          />
                        </List.Item>
                      );
                    }}
                  />
                ),
              },
            ]}
          />
        </div>
      )}

      {/* Main Content */}
      <div className={styles.mainContent}>
        <Row gutter={[16, 16]} className={styles.contentRow}>
          {/* Order List */}
          <Col xs={24} lg={6} className={styles.listColumn}>
            <Card
              title={`${t("orders")} (${cardOrders.length})`}
              size="small"
              styles={{ body: { padding: 0, maxHeight: 500, overflowY: "auto" } }}
            >
              <TripOrderList
                orders={cardOrders}
                selectedOrderId={selectedOrderId}
                isEditable={isEditable}
                isLoading={isLoading}
                onOrdersReorder={handleOrdersReorder}
                onOrderRemove={handleOrderRemove}
                onOrderSelect={handleOrderSelect}
              />
            </Card>
          </Col>

          {/* Map */}
          <Col xs={24} lg={18} className={styles.mapColumn}>
            <Card
              title={t("route_map")}
              size="small"
              styles={{ body: { padding: 0, height: 500 } }}
            >
              <TripRouteMap
                storeLocation={storeLatLng}
                assignedOrders={mapAssignedOrders}
                unassignedOrders={mapUnassignedOrders}
                recommendedOrderIds={recommendedOrdersData.recommendedIds}
                selectedOrderId={selectedOrderId}
                isEditable={isEditable}
                mapPolylines={mapPolylines}
                routeStatus={routeStatus}
                onOrderSelect={handleOrderSelect}
                onOrderAdd={handleOrderAdd}
                onOrderRemove={handleOrderRemove}
              />
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default React.memo(TripRoutePlanner);
